import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row } from "antd";
import styled from "styled-components/macro";
import "styled-components/macro"; // DO NOT REMOVE. Necessary for using the css={`...`} prop
import { useGridOptions } from "./gridOptions";
import {
  useAutoSizeAll,
  useExportToCsv,
  useGetAllGridColumns,
  useGetAreAllColumnsVisible,
  useSetQuickFilterText
} from "./gridApi";
import { useBoolean } from "../../helpers/useBoolean";
import { VisibilityModal } from "./show-hide-columns";
import { useToggle } from "react-use";
import {
  getStateOfGrid,
  MyAgGridReact,
  restoreStateToGrid
} from "./my-ag-grid-react";
import { getGridHeight } from "./getGridHeight";
import _ from "lodash";
import { Ribbon } from "./ribbon";
import { CSS_TO_ALWAYS_SHOW_SCROLLBARS, useExtraGridCss } from "./styling";
import { Classes } from "@blueprintjs/core";
import { SearchBoxStore } from "./search-box-store";
import { AG_TABLE_RIBBON_HEIGHT_PX } from "../../styles/constants";
import { NoData } from "./no-data";
import { useFullScreenModeEnabled } from "../../store/model-misc";
import { useGridBorderRadius } from "./use-grid-border-radius";
import { GridContextProvider, useTableName } from "./my-ag-grid-react-context";

function AgTable({ rowData, columnDefs, onCellClicked, height, tableName }) {
  const hasRowsToShow =
    rowData && columnDefs && _.size(rowData) && _.size(columnDefs);

  if (hasRowsToShow) {
    return (
      <SearchBoxStore.Provider>
        <GridContextProvider tableName={tableName}>
          <AgTableInner
            rowData={rowData}
            columnDefs={columnDefs}
            onCellClicked={onCellClicked}
            height={height}
            tableName={tableName}
          />
        </GridContextProvider>
      </SearchBoxStore.Provider>
    );
  } else {
    return <NoData />;
  }
}

function AgTableInner({ rowData, columnDefs, onCellClicked, height }) {
  const g = useGrid({ onCellClicked });

  const extraGridCss = useExtraGridCss(columnDefs);
  const fullScreen = useFullScreenModeEnabled();
  const borderRadius = useGridBorderRadius();
  const tableName = useTableName();

  return (
    <>
      <Col
        data-testid={`${tableName}_table_root`}
        className={`${Classes.ELEVATION_4}`}
        xs={g.vmShown ? 18 : 24}
        css={`
          & .ag-center-cols-viewport {
            background-color: #293742;
          }
          border-radius: ${borderRadius};
        `}
      >
        <Ribbon
          g={g}
          rowData={rowData}
          colDefs={columnDefs}
          tableName={tableName}
        />
        <Row data-testid={`${tableName}_table_content`}>
          <div
            css={`
              ${extraGridCss}
            `}
          >
            <g.GridWrapper
              height={height}
              fullScreen={fullScreen}
              tableName={tableName}
            >
              <MyAgGridReact
                gridRef={g.gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                gridOptions={g.gridOptions}
              />
            </g.GridWrapper>
          </div>
        </Row>
      </Col>
      {g.vmShown &&
        g.gridIsReady &&
        g.firstDataIsRendered &&
        _.size(g.columns) && (
          <>
            <Col xs={1}>
              <></>
            </Col>
            <Col
              xs={5}
              css={
                height &&
                `
                height: calc(${height} + ${AG_TABLE_RIBBON_HEIGHT_PX});
              `
              }
            >
              <VisibilityModal
                columns={g.columns}
                showColumn={g.showColumn}
                hideColumn={g.hideColumn}
              />
            </Col>
          </>
        )}
    </>
  );
}

const GridInnerWrapper = styled.div`
  height: ${getGridHeight};
  width: 100%;

  .ag-root[role="grid"] {
    border: none;
  }

  ${({ alwaysShowScrollbars }) =>
    alwaysShowScrollbars && CSS_TO_ALWAYS_SHOW_SCROLLBARS}
`;

function useGrid({ onCellClicked }) {
  const gridRef = useRef();

  const [localGridState, setLocalGridState] = useState({});
  const getLocalGridState = useCallback(() => localGridState, [localGridState]);
  const setLocalGridStateFromActualGridState = useCallback(() => {
    setLocalGridState(getStateOfGrid(gridRef.current));
  }, [setLocalGridState]);

  const getAllGridColumns = useGetAllGridColumns(gridRef);

  // TODO: Just use easy-peasy for all table state
  const [columns, setColumns] = useState([]);
  const refreshColumns = () => {
    setColumns(getAllGridColumns().map(c => ({ ...c })));
  };
  useEffect(() => {
    if (!_.size(columns)) {
      refreshColumns();
    }
    // eslint-disable-next-line
  }, [_.size(columns)]);

  const gridIsReady = useBoolean(true);
  const firstDataIsRendered = useBoolean(false);
  const exportToCsv = useExportToCsv(gridRef);
  const autoSizeAll = useAutoSizeAll(gridRef);
  const showColumn = useCallback(
    column => {
      // noinspection JSUnresolvedVariable
      gridRef.current.columnApi.setColumnVisible(column, true);
      refreshColumns();
    },
    // eslint-disable-next-line
    [gridRef, getAllGridColumns]
  );
  const hideColumn = useCallback(
    column => {
      // noinspection JSUnresolvedVariable
      gridRef.current.columnApi.setColumnVisible(column, false);
      refreshColumns();
    },
    // eslint-disable-next-line
    [gridRef, getAllGridColumns]
  );
  const getAreAllColumnsVisible = useGetAreAllColumnsVisible(gridRef);
  const [vmShown, toggleVmShown] = useToggle(false);
  const setQuickFilterText = useSetQuickFilterText(gridRef);
  const setCategoryFilter = useSetCategoryFilter(gridRef);

  const alwaysShowScrollbars = useBoolean(false);
  const handleToggleScrollbarsVisible = useCallback(() => {
    setLocalGridStateFromActualGridState();
    alwaysShowScrollbars.toggle();
    // eslint-disable-next-line
  }, [alwaysShowScrollbars.toggle, setLocalGridStateFromActualGridState]);

  useEffect(() => {
    if (_.size(getLocalGridState())) {
      restoreStateToGrid(getLocalGridState(), gridRef.current);
    }
    // eslint-disable-next-line
  }, [getLocalGridState()]);

  const gridOptions = useGridOptions({
    onCellClicked,
    onFirstDataRendered: useCallback(() => {
      firstDataIsRendered.setTrue();
      // eslint-disable-next-line
    }, [firstDataIsRendered.setTrue]),
    onColumnVisible: useCallback(() => {
      refreshColumns();
      // eslint-disable-next-line
    }, [setColumns, getAllGridColumns]),
    scrollbarWidth: alwaysShowScrollbars.value ? 8 : undefined
  });

  useEffect(() => {
    firstDataIsRendered.value && setTimeout(autoSizeAll, 300);
    // eslint-disable-next-line
  }, [firstDataIsRendered.value]);

  const GridWrapper = useCallback(
    ({ children, height, fullScreen, tableName }) => {
      return (
        <div
          css={`
            width: 100%;
            height: 100%;
          `}
        >
          <GridInnerWrapper
            className="ag-theme-balham-dark"
            alwaysShowScrollbars={alwaysShowScrollbars.value}
            height={height}
            fullScreen={fullScreen}
            tableName={tableName}
          >
            {children}
          </GridInnerWrapper>
        </div>
      );
    },
    // eslint-disable-next-line
    [alwaysShowScrollbars.value]
  );

  return {
    gridRef,
    gridOptions,
    GridWrapper,
    exportToCsv,
    autoSizeAll,
    showColumn,
    hideColumn,
    getAllGridColumns,
    getAreAllColumnsVisible,
    vmShown,
    toggleVmShown,
    VisibilityModal,
    setQuickFilterText,
    toggleScrollbarsVisible: handleToggleScrollbarsVisible,
    gridIsReady,
    firstDataIsRendered,
    mySetStateOfGrid: setLocalGridStateFromActualGridState,
    columns,
    setCategoryFilter
  };
}

function useSetCategoryFilter(gridRef) {
  return (colId, filterValue) => {
    // console.log("useSetCategoryFilter");
    console.log({ filterModel: gridRef.current.api.getFilterModel() });
    const api = gridRef.current.api;
    api.setFilterModel({
      ...gridRef.current.api.getFilterModel(),
      [colId]: { value: filterValue }
    });
  };
}

export default AgTable;
